import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { LayoutDashboard, Inbox, Car, Tag, Gavel, Megaphone, Download, Check, X, ShieldCheck, LogOut, Lock, Unlock } from "lucide-react";
import { api, API, endpoints } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Wordmark } from "@/components/Wordmark";

const TABS = [
  { id: "overview", label: "Overview", icon: LayoutDashboard },
  { id: "enquiries", label: "Enquiries", icon: Inbox },
  { id: "cars", label: "Cars", icon: Car },
  { id: "categories", label: "Categories", icon: Tag },
  { id: "judges", label: "Judges", icon: Gavel },
  { id: "announce", label: "Announce", icon: Megaphone },
];

export default function Admin() {
  const { user, loading, login, logout, hasRole } = useAuth();
  const [tab, setTab] = useState("overview");

  if (loading) return <div className="min-h-screen grid place-items-center bg-[#0f0f11] text-[#8a8d91] font-heading uppercase">Loading...</div>;
  if (!user) return <Gate onLogin={login} text="Sign in to access admin" />;
  if (!hasRole("admin")) return <Gate text="You need admin access. Contact the organiser." onLogin={null} />;

  return (
    <div className="min-h-screen bg-[#0f0f11]">
      <header className="glass sticky top-0 z-40 border-b border-white/10">
        <div className="max-w-6xl mx-auto px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Wordmark size="text-lg" showArrow={false} />
            <span className="font-heading uppercase text-xs text-[#b026ff] border border-[#b026ff]/40 px-2 py-0.5">Admin</span>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/app" className="text-[#8a8d91] hover:text-white font-heading uppercase text-xs">App</Link>
            <button onClick={logout} className="text-[#8a8d91] hover:text-[#e8321c]"><LogOut size={18} /></button>
          </div>
        </div>
      </header>
      <div className="max-w-6xl mx-auto px-4 py-6 grid lg:grid-cols-[200px_1fr] gap-6">
        <nav className="flex lg:flex-col gap-1 overflow-x-auto no-scrollbar">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`admin-tab-${t.id}`}
              className={`flex items-center gap-2 px-3 py-2.5 font-heading uppercase text-sm shrink-0 transition-colors ${tab === t.id ? "pas-gradient text-black" : "text-white/70 hover:text-white border border-white/10 lg:border-transparent"}`}>
              <t.icon size={16} /> {t.label}
            </button>
          ))}
        </nav>
        <div>
          {tab === "overview" && <Overview />}
          {tab === "enquiries" && <Enquiries />}
          {tab === "cars" && <CarsReview />}
          {tab === "categories" && <Categories />}
          {tab === "judges" && <Judges />}
          {tab === "announce" && <Announce />}
        </div>
      </div>
    </div>
  );
}

function Gate({ text, onLogin }) {
  return (
    <div className="min-h-screen grid place-items-center bg-[#0f0f11] px-6">
      <div className="text-center border border-white/10 bg-[#121212] p-10 max-w-sm">
        <ShieldCheck className="mx-auto text-[#b026ff] mb-4" size={40} />
        <p className="text-white font-heading uppercase mb-4">{text}</p>
        {onLogin && <button onClick={onLogin} className="pas-btn" data-testid="admin-login-btn">Sign in with Google</button>}
      </div>
    </div>
  );
}

function Overview() {
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {}); }, []);
  if (!stats) return null;
  const cards = [
    { label: "Enquiries", value: stats.enquiries }, { label: "Cars pending", value: stats.cars_pending },
    { label: "Total cars", value: stats.cars_total }, { label: "Votes cast", value: stats.votes },
    { label: "Tickets sold", value: stats.tickets }, { label: "Revenue AUD", value: `$${stats.revenue.toFixed(0)}` },
  ];
  return (
    <div>
      <h2 className="font-display text-3xl uppercase text-white mb-4">Overview</h2>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="admin-stats">
        {cards.map((c) => (
          <div key={c.label} className="border border-white/10 bg-[#121212] p-5">
            <div className="font-display text-5xl pas-gradient-text">{c.value}</div>
            <div className="font-heading uppercase text-xs text-[#8a8d91] mt-1">{c.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Enquiries() {
  const [rows, setRows] = useState([]);
  const [kind, setKind] = useState("");
  const load = useCallback(() => api.get("/admin/enquiries", { params: kind ? { kind } : {} }).then((r) => setRows(r.data)).catch(() => {}), [kind]);
  useEffect(() => { load(); }, [load]);
  const setStatus = async (id, status) => { await api.post(`/admin/enquiries/${id}/status?status=${status}`); load(); };
  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-display text-3xl uppercase text-white">Enquiries</h2>
        <a href={`${API}/admin/enquiries.csv`} className="pas-btn-outline text-xs py-2 px-3" data-testid="csv-export"><Download size={14} /> CSV</a>
      </div>
      <div className="flex gap-2 mb-4 overflow-x-auto no-scrollbar">
        {["", "waitlist", "exhibit-ride", "exhibitor", "sponsor", "contact"].map((k) => (
          <button key={k} onClick={() => setKind(k)} className={`px-3 py-1.5 font-heading uppercase text-xs border shrink-0 ${kind === k ? "pas-gradient text-black border-transparent" : "border-white/15 text-white/70"}`}>{k || "All"}</button>
        ))}
      </div>
      <div className="space-y-2" data-testid="enquiries-list">
        {rows.map((r) => (
          <div key={r.id} className="border border-white/10 bg-[#121212] p-4">
            <div className="flex items-start justify-between gap-2">
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-heading uppercase text-xs text-[#b026ff]">{r.kind}</span>
                  <span className={`text-[10px] uppercase font-heading px-1.5 py-0.5 ${r.status === "new" ? "bg-[#ffd600] text-black" : "bg-white/10 text-white/60"}`}>{r.status}</span>
                </div>
                <div className="font-heading text-white mt-1">{r.name} · <span className="text-[#8a8d91] font-body text-sm">{r.email}</span></div>
                {r.message && <div className="text-[#8a8d91] text-sm font-body mt-1">{r.message}</div>}
                {r.interest && <div className="text-xs text-[#ff7a1a] mt-1">Interest: {r.interest}</div>}
              </div>
              <div className="flex gap-1 shrink-0">
                <button onClick={() => setStatus(r.id, "actioned")} className="p-1.5 border border-white/15 text-[#3ddc84] hover:bg-white/5" title="Mark actioned"><Check size={14} /></button>
                <button onClick={() => setStatus(r.id, "archived")} className="p-1.5 border border-white/15 text-[#8a8d91] hover:bg-white/5" title="Archive"><X size={14} /></button>
              </div>
            </div>
          </div>
        ))}
        {rows.length === 0 && <div className="text-[#8a8d91] font-body py-8 text-center">No enquiries</div>}
      </div>
    </div>
  );
}

function CarsReview() {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("submitted");
  const load = useCallback(() => api.get("/admin/cars", { params: filter ? { status: filter } : {} }).then((r) => setRows(r.data)).catch(() => {}), [filter]);
  useEffect(() => { load(); }, [load]);
  const setStatus = async (id, status) => { await api.post(`/admin/cars/${id}/status?status=${status}`); toast.success(`Marked ${status}`); load(); };
  const verify = async (id) => { await api.post(`/admin/cars/${id}/verify-render`); toast.success("Render verified"); load(); };
  const statuses = ["submitted", "approved", "displayed", "winner", "rejected", ""];
  return (
    <div>
      <h2 className="font-display text-3xl uppercase text-white mb-4">Cars Review</h2>
      <div className="flex gap-2 mb-4 overflow-x-auto no-scrollbar">
        {statuses.map((s) => (
          <button key={s} onClick={() => setFilter(s)} className={`px-3 py-1.5 font-heading uppercase text-xs border shrink-0 ${filter === s ? "pas-gradient text-black border-transparent" : "border-white/15 text-white/70"}`}>{s || "All"}</button>
        ))}
      </div>
      <div className="space-y-3" data-testid="admin-cars-list">
        {rows.map((c) => (
          <div key={c.id} className="flex gap-3 border border-white/10 bg-[#121212] overflow-hidden" data-testid={`admin-car-${c.id}`}>
            {c.photo_url && <img src={c.photo_url} alt="" className="w-28 h-28 object-cover" />}
            <div className="py-3 pr-3 flex-1">
              <div className="font-heading text-white uppercase">{c.year} {c.make} {c.model}</div>
              <div className="text-xs text-[#8a8d91]">{c.category_name} · by {c.owner_name} · {c.status}</div>
              {c.is_ai_generated && (
                <div className="text-xs mt-1 text-[#b026ff]">AI render · {c.render_verified_by_admin ? "verified" : <button onClick={() => verify(c.id)} className="underline">verify now</button>}</div>
              )}
              <div className="flex flex-wrap gap-1.5 mt-2">
                <button onClick={() => setStatus(c.id, "approved")} className="px-2 py-1 text-xs font-heading uppercase border border-[#3ddc84]/50 text-[#3ddc84]">Approve</button>
                <button onClick={() => setStatus(c.id, "displayed")} className="px-2 py-1 text-xs font-heading uppercase border border-white/20 text-white/80">Display</button>
                <button onClick={() => setStatus(c.id, "winner")} className="px-2 py-1 text-xs font-heading uppercase border border-[#ffd600]/50 text-[#ffd600]">Winner</button>
                <button onClick={() => setStatus(c.id, "rejected")} className="px-2 py-1 text-xs font-heading uppercase border border-[#e8321c]/50 text-[#e8321c]">Reject</button>
              </div>
            </div>
          </div>
        ))}
        {rows.length === 0 && <div className="text-[#8a8d91] font-body py-8 text-center">No cars</div>}
      </div>
    </div>
  );
}

function Categories() {
  const [rows, setRows] = useState([]);
  const [name, setName] = useState("");
  const load = () => endpoints.categories().then(setRows).catch(() => {});
  useEffect(() => { load(); }, []);
  const add = async () => { if (!name) return; await api.post("/admin/categories", { name }); setName(""); toast.success("Category added"); load(); };
  const lock = async (id, locked) => { await api.post(`/admin/categories/${id}/lock?locked=${locked}`); load(); };
  return (
    <div>
      <h2 className="font-display text-3xl uppercase text-white mb-4">Categories</h2>
      <div className="flex gap-2 mb-4">
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="New category name" className="flex-1 bg-[#0f0f11] border border-white/15 px-4 py-2.5 text-white outline-none focus:border-[#b026ff]" data-testid="new-category-input" />
        <button onClick={add} className="pas-btn text-sm" data-testid="add-category-btn">Add</button>
      </div>
      <div className="space-y-2">
        {rows.map((c) => (
          <div key={c.id} className="flex items-center justify-between border border-white/10 bg-[#121212] px-4 py-3">
            <div>
              <div className="font-heading text-white uppercase">{c.name}</div>
              <div className="text-xs text-[#8a8d91]">{c.description}</div>
            </div>
            <button onClick={() => lock(c.id, !c.locked)} className={`px-3 py-1.5 font-heading uppercase text-xs flex items-center gap-1 border ${c.locked ? "border-[#e8321c]/50 text-[#e8321c]" : "border-white/15 text-white/70"}`}>
              {c.locked ? <><Lock size={13} /> Locked</> : <><Unlock size={13} /> Open</>}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function Judges() {
  const [rows, setRows] = useState([]);
  const [email, setEmail] = useState("");
  const load = () => api.get("/admin/judges").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const add = async () => { if (!email) return; try { await api.post(`/admin/users/${email}/role?role=judge`); toast.success("Judge added"); setEmail(""); load(); } catch { toast.error("User must sign in first"); } };
  return (
    <div>
      <h2 className="font-display text-3xl uppercase text-white mb-4">Judges Roster</h2>
      <div className="flex gap-2 mb-4">
        <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="User email to promote" className="flex-1 bg-[#0f0f11] border border-white/15 px-4 py-2.5 text-white outline-none focus:border-[#b026ff]" data-testid="judge-email-input" />
        <button onClick={add} className="pas-btn text-sm" data-testid="add-judge-btn">Add judge</button>
      </div>
      <div className="space-y-2">
        {rows.map((u) => (
          <div key={u.user_id} className="flex items-center gap-3 border border-white/10 bg-[#121212] px-4 py-3">
            {u.picture ? <img src={u.picture} alt="" className="w-9 h-9 rounded-full" /> : <span className="w-9 h-9 rounded-full pas-gradient grid place-items-center text-black font-bold">{u.name?.[0]}</span>}
            <div>
              <div className="font-heading text-white">{u.name}</div>
              <div className="text-xs text-[#8a8d91]">{u.email} · {u.roles?.join(", ")}</div>
            </div>
          </div>
        ))}
        {rows.length === 0 && <div className="text-[#8a8d91] font-body py-8 text-center">No judges yet</div>}
      </div>
    </div>
  );
}

function Announce() {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [rows, setRows] = useState([]);
  const load = () => endpoints.announcements().then(setRows).catch(() => {});
  useEffect(() => { load(); }, []);
  const post = async () => { if (!title || !body) return toast.error("Title and body required"); await api.post("/admin/announcements", { title, body }); setTitle(""); setBody(""); toast.success("Announcement posted"); load(); };
  return (
    <div>
      <h2 className="font-display text-3xl uppercase text-white mb-4">Announcements</h2>
      <div className="border border-white/10 bg-[#121212] p-4 space-y-3 mb-5">
        <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title" className="w-full bg-[#0f0f11] border border-white/15 px-4 py-2.5 text-white outline-none focus:border-[#b026ff]" data-testid="announce-title" />
        <textarea value={body} onChange={(e) => setBody(e.target.value)} placeholder="Message" rows={3} className="w-full bg-[#0f0f11] border border-white/15 px-4 py-2.5 text-white outline-none focus:border-[#b026ff]" data-testid="announce-body" />
        <button onClick={post} className="pas-btn text-sm" data-testid="post-announce-btn"><Megaphone size={15} /> Post announcement</button>
      </div>
      <div className="space-y-2">
        {rows.map((a) => (
          <div key={a.id} className="border-l-2 border-[#b026ff] bg-[#121212] px-4 py-3">
            <div className="font-heading text-white uppercase">{a.title}</div>
            <div className="text-[#8a8d91] text-sm font-body">{a.body}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
